import type { FormValues } from '../types';
import {
  EMPLOYMENT_TYPE_MAPPING,
  REQUISITION_TYPE_MAPPING,
  ENUM_DEFAULTS,
} from '../types';

const processOffices = (
  currUserSelected: number[] | undefined,
  dataFromApi: any[] | undefined
) => {
  if (!Array.isArray(currUserSelected) || !currUserSelected?.length) {
    // If no offices selected but there were offices before, mark all as destroyed
    if (dataFromApi && dataFromApi.length > 0) {
      return dataFromApi.map((item) => ({
        ats_office_id: item?.id,
        _destroy: '1',
      }));
    }
    return [];
  }

  const defaultOfficesId = dataFromApi?.map((i) => i?.id) || [];

  const commonToBoth = currUserSelected.filter((value) =>
    defaultOfficesId.includes(value)
  );

  const itemsToKeep = currUserSelected.filter(
    (value) => !defaultOfficesId.includes(value)
  );

  const itemsToRemove = defaultOfficesId.filter(
    (value) => !currUserSelected.includes(value)
  );

  // Mapping common values and unique values from current selection
  const toAppend = [...commonToBoth, ...itemsToKeep].map((item) => ({
    ats_office_id: item,
  }));

  // Mapping removed values with _destroy property
  const toDestroy = itemsToRemove.map((item) => ({
    ats_office_id: item,
    _destroy: '1',
  }));

  return [...toAppend, ...toDestroy];
};

export const generateRequisitionFormData = (
  values: FormValues,
  initialRequisitionData?: any
): Record<string, any> => {
  const payload: Record<string, any> = {
    role_name: values.role_name || '',
    employment_type: EMPLOYMENT_TYPE_MAPPING[values.work_type] || 0,
    requisition_type: REQUISITION_TYPE_MAPPING[values.requisition_type] || 0,
    description: values.description || '',
  };

  if (values.department) payload.ats_department_id = values.department;
  payload.minimum_salary = values.min_salary || '';
  payload.maximum_salary = values.max_salary || '';
  if (values.currency) payload.salary_currency_id = values.currency;

  // Handle office with _destroy for removed offices
  const initialOffices = initialRequisitionData?.ats_offices;
  const offices = processOffices(values.office, initialOffices);
  if (offices?.length > 0) {
    payload.requisition_offices_attributes = offices;
  }

  if (values.recruiter) payload.recruiter_id = values.recruiter;
  if (values.hiring_manager) payload.hiring_manager_id = values.hiring_manager;
  if (values.target_hire_date)
    payload.target_hire_date = values.target_hire_date;
  if (values.target_start_date)
    payload.target_start_date = values.target_start_date;

  payload.open_after_approval = false;
  payload.confidential = values.is_confidential || false;

  if (values.associated_jobs) payload.ats_job_id = values.associated_jobs;

  // Handle head_count - only send if hiring_multiple_candidates is true
  if (values.hiring_multiple_candidates) {
    payload.head_count = values.head_count;
  }

  // Handle custom fields - expecting signed_id strings or existing file objects
  const additionalInfo: Record<string, any> = {};

  Object.keys(values).forEach((key) => {
    if (key.startsWith('custom_')) {
      const fieldName = key.replace('custom_', '');
      const fieldValue = values[key];

      // Skip null/undefined values - don't send the key at all
      if (fieldValue === null || fieldValue === undefined) {
        return;
      }

      // Skip empty strings - don't send the key at all
      if (fieldValue === '') {
        return;
      }

      if (Array.isArray(fieldValue)) {
        // Empty array means file was removed - don't send the key at all
        if (fieldValue.length === 0) {
          return;
        }

        // For file fields: if it's an array of objects with 'id' and 'url' (existing files),
        // extract just the signed_id or id
        const hasExistingFiles = fieldValue.some(
          (item) =>
            item &&
            typeof item === 'object' &&
            'url' in item &&
            'filename' in item
        );

        if (hasExistingFiles) {
          // Extract IDs from existing file objects
          // If signed_id exists, use it; otherwise use id
          const fileIds = fieldValue
            .map((item) => {
              if (typeof item === 'string') return item; // Already a signed_id
              if (item && typeof item === 'object') {
                return item.signed_id || item.id;
              }
              return null;
            })
            .filter(Boolean);

          // Don't send the key if all files were removed
          if (fileIds.length === 0) {
            return;
          }

          additionalInfo[fieldName] =
            fileIds.length === 1 ? fileIds[0] : fileIds;
        } else {
          // New files (signed_ids) or other array values
          additionalInfo[fieldName] = fieldValue;
        }
      } else {
        additionalInfo[fieldName] = fieldValue;
      }
    }
  });

  if (Object.keys(additionalInfo).length > 0) {
    payload.additional_info = additionalInfo;
  }

  return payload;
};

export const getInitialRequisitionValues = (): Partial<FormValues> => {
  return {
    role_name: '',
    work_type: ENUM_DEFAULTS.employment_type,
    requisition_type: ENUM_DEFAULTS.requisition_type,
    description: '',
    department: undefined,
    office: undefined,
    min_salary: undefined,
    max_salary: undefined,
    currency: 5, // USD default
    recruiter: undefined,
    hiring_manager: undefined,
    target_hire_date: '',
    target_start_date: '',
    is_confidential: false,
    associated_jobs: undefined,
    hiring_multiple_candidates: false,
    head_count: 2,
  };
};

/**
 * Transform API requisition data to form values for editing
 */
export const transformRequisitionToFormValues = (
  requisition: any,
  customFields?: any[]
): Partial<FormValues> => {
  const formValues: Partial<FormValues> = {
    role_name: requisition.role_name || '',
    work_type: requisition.employment_type,
    requisition_type: requisition.requisition_type,
    description: requisition.description || '',
    department: requisition.ats_department?.id,
    office: requisition.ats_offices?.map((office: any) => office.id) || [],
    min_salary: requisition.minimum_salary,
    max_salary: requisition.maximum_salary,
    currency: requisition.salary_currency?.id || 5,
    recruiter: requisition.recruiter?.id,
    hiring_manager: requisition.hiring_manager?.id,
    target_hire_date: requisition.target_hire_date || '',
    target_start_date: requisition.target_start_date || '',
    is_confidential: requisition.confidential || false,
    associated_jobs: requisition.ats_job?.id,
    head_count: requisition.group_size || 2,
    hiring_multiple_candidates: requisition.group_size > 1,
    requisition_id: requisition.code,
  };

  // Handle custom fields from additional_info
  if (requisition.additional_info) {
    Object.entries(requisition.additional_info).forEach(([key, value]) => {
      // Special handling for file fields (rel_file)
      // File upload component expects an array, even for single files
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        'url' in value &&
        'filename' in value
      ) {
        // This is a file object, wrap it in an array
        formValues[`custom_${key}`] = [value];
      } else {
        // For all other field types, use as-is
        formValues[`custom_${key}`] = value;
      }
    });
  }

  return formValues;
};
